// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

// Coin Package Types
export interface CoinPackage {
  id: string;
  name: string;
  description: string;
  coinAmount: number;
  price: number;
  currency: string;
  bonusCoins: number;
  enabled: boolean;
  featured: boolean;
  discountPercentage?: number;
  originalPrice?: number;
  icon: string;
  productId: string; // For app store integration
  platformPricing: {
    android: PlatformPricing;
    ios: PlatformPricing;
  };
  purchaseCount: number;
  revenue: number;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformPricing {
  productId: string;
  price: number;
  currency: string;
  localizedPrice: string;
}

// Bonus System Types
export enum BonusType {
  SIGNUP = 'signup',
  REFERRAL = 'referral',
  DAILY_LOGIN = 'daily_login',
  ACHIEVEMENT = 'achievement',
  EVENT = 'event',
  ADMIN_GIFT = 'admin_gift',
  COMPENSATION = 'compensation'
}

export interface BonusConfig {
  id: string;
  type: BonusType;
  name: string;
  description: string;
  coinAmount: number;
  enabled: boolean;
  conditions?: BonusConditions;
  cooldownHours?: number;
  maxClaims?: number;
  validFrom?: string;
  validUntil?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BonusConditions {
  minLevel?: number;
  requiredGames?: string[];
  minGamesPlayed?: number;
  referralRequirements?: {
    minReferrals: number;
    referralMustPlay: boolean;
  };
  achievementId?: string;
  eventId?: string;
}

// Transaction Types
export enum TransactionType {
  PURCHASE = 'purchase',
  EARNED = 'earned',
  SPENT = 'spent',
  BONUS = 'bonus',
  ADMIN_ADJUSTMENT = 'admin_adjustment',
  REFUND = 'refund'
}

export enum TransactionSource {
  GAME_LUDO = 'game_ludo',
  GAME_QUIZ = 'game_quiz',
  GAME_SOLITAIRE = 'game_solitaire',
  STORE_PURCHASE = 'store_purchase',
  AD_REWARD = 'ad_reward',
  BONUS_SIGNUP = 'bonus_signup',
  BONUS_REFERRAL = 'bonus_referral',
  BONUS_DAILY = 'bonus_daily',
  ADMIN_MANUAL = 'admin_manual',
  LIFELINE_PURCHASE = 'lifeline_purchase',
  POWERUP_PURCHASE = 'powerup_purchase'
}

export interface CoinTransaction {
  id: string;
  userId: string;
  username: string;
  type: TransactionType;
  source: TransactionSource;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  description: string;
  metadata?: {
    gameId?: string;
    packageId?: string;
    bonusId?: string;
    adminId?: string;
    referenceId?: string;
  };
  timestamp: string;
}

// Manual Adjustment Types
export interface ManualAdjustment {
  userId: string;
  amount: number;
  reason: string;
  adminId: string;
  adminUsername: string;
  type: 'credit' | 'debit';
}

export interface BulkAdjustment {
  userIds: string[];
  amount: number;
  reason: string;
  adminId: string;
  adminUsername: string;
  type: 'credit' | 'debit';
}

// Event System Types
export interface CoinEvent {
  id: string;
  name: string;
  description: string;
  type: 'multiplier' | 'bonus' | 'discount';
  enabled: boolean;
  startDate: string;
  endDate: string;
  configuration: EventConfiguration;
  participantCount: number;
  totalCoinsDistributed: number;
  createdAt: string;
  updatedAt: string;
}

export interface EventConfiguration {
  multiplier?: number; // e.g., 2x coins for weekend
  bonusAmount?: number; // flat bonus coins
  discountPercentage?: number; // discount on coin packages
  applicableGames?: string[];
  applicablePackages?: string[];
  maxParticipants?: number;
  requirementType?: 'games_played' | 'coins_spent' | 'login_streak';
  requirementValue?: number;
}

// Analytics Types
export interface CoinEconomyAnalytics {
  totalCoinsInCirculation: number;
  totalCoinsEarned: number;
  totalCoinsSpent: number;
  totalCoinsPurchased: number;
  averageCoinsPerUser: number;
  coinVelocity: number; // coins spent / coins earned ratio
  topSpenders: UserCoinStats[];
  topEarners: UserCoinStats[];
  transactionsBySource: {
    [key in TransactionSource]: {
      count: number;
      totalAmount: number;
    };
  };
  packagePerformance: PackagePerformance[];
  bonusDistribution: BonusDistribution[];
  dailyStats: DailyCoinStats[];
}

export interface UserCoinStats {
  userId: string;
  username: string;
  totalCoins: number;
  coinsEarned: number;
  coinsSpent: number;
  coinsPurchased: number;
  lastActivity: string;
}

export interface PackagePerformance {
  packageId: string;
  packageName: string;
  purchaseCount: number;
  revenue: number;
  conversionRate: number;
  averageTimeToPurchase: number; // in hours
}

export interface BonusDistribution {
  bonusType: BonusType;
  totalClaimed: number;
  totalCoinsDistributed: number;
  uniqueClaimers: number;
  averageClaimTime: number; // in hours
}

export interface DailyCoinStats {
  date: string;
  coinsEarned: number;
  coinsSpent: number;
  coinsPurchased: number;
  activeUsers: number;
  newUsers: number;
}

// API Request/Response Types
export interface CreateCoinPackageRequest {
  name: string;
  description: string;
  coinAmount: number;
  price: number;
  currency: string;
  bonusCoins: number;
  enabled: boolean;
  featured: boolean;
  discountPercentage?: number;
  icon: string;
  productId: string;
  platformPricing: {
    android: PlatformPricing;
    ios: PlatformPricing;
  };
}

export interface UpdateCoinPackageRequest extends Partial<CreateCoinPackageRequest> {
  id: string;
}

export interface CreateBonusConfigRequest {
  type: BonusType;
  name: string;
  description: string;
  coinAmount: number;
  enabled: boolean;
  conditions?: BonusConditions;
  cooldownHours?: number;
  maxClaims?: number;
  validFrom?: string;
  validUntil?: string;
}

export interface UpdateBonusConfigRequest extends Partial<CreateBonusConfigRequest> {
  id: string;
}

export interface CreateCoinEventRequest {
  name: string;
  description: string;
  type: 'multiplier' | 'bonus' | 'discount';
  enabled: boolean;
  startDate: string;
  endDate: string;
  configuration: EventConfiguration;
}

export interface UpdateCoinEventRequest extends Partial<CreateCoinEventRequest> {
  id: string;
}

// List Response Types
export interface CoinPackageList {
  packages: CoinPackage[];
  totalCount: number;
}

export interface BonusConfigList {
  bonuses: BonusConfig[];
  totalCount: number;
}

export interface CoinTransactionList {
  transactions: CoinTransaction[];
  totalCount: number;
  nextCursor?: string;
}

export interface CoinEventList {
  events: CoinEvent[];
  totalCount: number;
}

// Dashboard Types
export interface CoinManagementDashboard {
  packages: CoinPackage[];
  bonusConfigs: BonusConfig[];
  activeEvents: CoinEvent[];
  recentTransactions: CoinTransaction[];
  analytics: CoinEconomyAnalytics;
  summary: {
    totalPackages: number;
    activePackages: number;
    totalBonuses: number;
    activeBonuses: number;
    activeEvents: number;
    dailyRevenue: number;
    dailyTransactions: number;
    totalCoinsInCirculation: number;
  };
}

// Search and Filter Types
export interface CoinPackageFilters {
  enabled?: boolean;
  featured?: boolean;
  priceRange?: {
    min: number;
    max: number;
  };
  coinRange?: {
    min: number;
    max: number;
  };
}

export interface TransactionFilters {
  userId?: string;
  type?: TransactionType;
  source?: TransactionSource;
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  amountRange?: {
    min: number;
    max: number;
  };
}

export interface BonusFilters {
  type?: BonusType;
  enabled?: boolean;
  active?: boolean; // within valid date range
}

export interface EventFilters {
  type?: 'multiplier' | 'bonus' | 'discount';
  enabled?: boolean;
  active?: boolean; // currently running
  dateRange?: {
    startDate: string;
    endDate: string;
  };
}

// Export Types
export interface CoinManagementExportOptions {
  format: 'excel' | 'pdf' | 'csv';
  includePackages: boolean;
  includeTransactions: boolean;
  includeBonuses: boolean;
  includeEvents: boolean;
  includeAnalytics: boolean;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  transactionFilters?: TransactionFilters;
}
